import type { FC } from 'react';
import { useSelector } from 'react-redux';

import { checkAndSendPageViewGAEvent } from '@src/helpers/analyticsEvents';
import { useCurrentLocation } from '@src/reducers/locations';
import { getCreditsMetadata } from '@src/reducers/microcredits';
import { selectItemSource } from '@src/reducers/product';
import { useSeoState } from '@src/reducers/seo';
import { isServer } from '@src/utils';
import { getCommonReplacements } from '@src/utils/meta';
import { SeoMeta } from 'entities/Seo';
import { BASE_URL } from 'entities/Seo/config';
import { getTitle, getDescription } from 'entities/Seo/lib';

export const SeoHead: FC<{ canonical?: string }> = ({ canonical }) => {
  const { pathname, seo } = useSeoState();
  const metadata = useSelector(getCreditsMetadata);
  const currentLocation = useCurrentLocation();
  const cardSource = useSelector(selectItemSource);

  if (pathname?.includes('mfo') && cardSource) {
    return (
      <SeoMeta
        schema={seo?.schema}
        indexingDisabled={seo?.indexingDisabled}
        title={getTitle(cardSource)}
        description={getDescription(cardSource)}
        canonical={seo?.canonical || `${BASE_URL}${pathname}`}
      />
    );
  }

  if (!seo?.title || !seo?.description) {
    return null;
  }

  if (!isServer) {
    checkAndSendPageViewGAEvent(seo.title, pathname);
  }

  return (
    <SeoMeta
      indexingDisabled={seo.indexingDisabled}
      title={getCommonReplacements({ template: seo.title, meta: metadata, location: currentLocation })}
      description={getCommonReplacements({
        template: seo.description,
        meta: metadata,
        location: currentLocation,
      })}
      canonical={seo?.canonical || `${BASE_URL}${pathname}`}
      ogDescription={seo.ogDescription}
      ogTitle={seo.ogTitle}
      schema={seo?.schema}
    />
  );
};
